/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCreateSessionParams } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../../device";
import { SessionInteractor } from "../../interactor";
import { PlaybackContentType, SessionState } from "../enums";
import type { ISessionCreateModel } from "../interfaces";
import { StreamingSessionImpl } from "./StreamingSessionImpl";

/**
 * Streaming session implementation for VOD content.
 */
export class VodStreamingSessionImpl extends StreamingSessionImpl {
  protected readonly TAG: string = "VodStreamingSessionImpl";

  /**
   * The root offer the session is created against. A preview (trailer) is
   * packaged under its own root offer, so a preview session asks for
   * `partnerPreviewRootOfferId`; the full title, or a preview whose watch
   * action names no preview root, uses `partnerRootOfferId`.
   */
  private static resolveRootOfferId(
    kernelFields: Record<string, unknown>,
    isPreview: boolean
  ): string | undefined {
    const previewRootOfferId = kernelFields.partnerPreviewRootOfferId;
    if (
      isPreview &&
      typeof previewRootOfferId === "string" &&
      previewRootOfferId !== ""
    ) {
      return previewRootOfferId;
    }
    const rootOfferId = kernelFields.partnerRootOfferId;
    return typeof rootOfferId === "string" ? rootOfferId : undefined;
  }

  public async createStreamingSession(
    sessionCreateModel: ISessionCreateModel
  ): Promise<void> {
    const { drmType, isPreview, transportEncodingType, vodWatchAction } =
      sessionCreateModel;

    const kernel =
      vodWatchAction !== undefined &&
      vodWatchAction !== null &&
      typeof vodWatchAction === "object" &&
      "kernel" in vodWatchAction
        ? (vodWatchAction.kernel as Record<string, unknown> | undefined)
        : undefined;
    const kernelFields: Record<string, unknown> = kernel || {};

    const partnerId = kernelFields.partnerId as string | undefined;
    const partnerRootOfferId = VodStreamingSessionImpl.resolveRootOfferId(
      kernelFields,
      isPreview === true
    );
    const partnerAssetId = kernelFields.partnerAssetId as string | undefined;

    if (!partnerId) {
      this.log(
        "error",
        "createStreamingSession(): missing partnerId in vodWatchAction"
      );
      this.mError = this.getPlaybackError("MISSING_PARTNER_ID");
      this.mSessionState = SessionState.ERROR;
      return;
    }

    try {
      const tsn = await DeviceModel.getTiVoSettings().getBodyId();
      const sessionParams: TCreateSessionParams = {
        deviceType:
          DeviceModel.getTiVoSettings().getDeviceType() as TCreateSessionParams["deviceType"],
        drmType: (drmType || "widevine") as TCreateSessionParams["drmType"],
        partnerId,
        partnerServicesCustomerId: await DeviceModel.getPartnerCustomerId(),
        partnerServicesBodyId: await DeviceModel.getCaDeviceId(),
        partnerRootOfferId: partnerRootOfferId || "",
        partnerAssetId: partnerAssetId || "",
        transportEncodingType:
          transportEncodingType as TCreateSessionParams["transportEncodingType"],
        tsn
      };

      this.log(
        "info",
        `createStreamingSession(): contentType=${sessionCreateModel.playbackContentType}` +
          ` isPreview=${String(isPreview === true)}` +
          ` partnerRootOfferId=${partnerRootOfferId || "<none>"}`
      );

      // Survives route changes, like the VOD metadata calls: the session is
      // the playback's, and a navigation abort would fail it as a session
      // error with nothing actually wrong.
      const session = await new SessionInteractor().createSession(
        sessionParams,
        { bypassNavigationCancellation: true }
      );
      this.mDomainSession = session;
      this.mSession = {
        playlistUrl: VodStreamingSessionImpl.resolvePlaylistUrl(
          session.playlistUrl,
          sessionCreateModel.playUri
        ),
        trickModeRestrictions:
          (session.trickModeRestrictions as Record<string, unknown>) || {},
        firstKeepAliveSeconds: session.firstKeepAliveSeconds,
        keepAliveIntervalSeconds: session.keepAliveIntervalSeconds || 0,
        localityId: VodStreamingSessionImpl.resolveLocalityId(session)
      };
      this.mSessionState = SessionState.SUCCESS;
    } catch (error: unknown) {
      this.log("error", "createStreamingSession()", error);
      let errorMessage = "";
      if (typeof error === "object" && error !== null && "message" in error) {
        const errorObject = error as { message: unknown };
        if (typeof errorObject.message === "string") {
          errorMessage = errorObject.message;
        }
      }

      if (errorMessage === "Network unavailable") {
        this.mSessionState = SessionState.UNREACHABLE;
      } else {
        this.mError = this.getSessionError(error, PlaybackContentType.VOD);
        this.mSessionState = SessionState.ERROR;
      }
    }
  }
}
